import { useState } from 'react';
import { CalendarClock } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useCoachMakeups, useScheduleMakeup, useUpdateMakeupStatus } from '@/hooks/use-makeups';
import { useTodaySessions } from '@/hooks/coach/use-coach-sessions';
import { MakeupsList } from '@/components/coach/makeups/makeups-list';
import { RequestMakeupSheet } from '@/components/coach/makeups/request-makeup-sheet';

export default function CoachMakeupsPage() {
  const { data: makeups, isLoading } = useCoachMakeups();
  const scheduleMakeup = useScheduleMakeup();
  const updateStatus = useUpdateMakeupStatus();
  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [selectedMakeupId, setSelectedMakeupId] = useState<string | null>(null);
  const [sessionDate, setSessionDate] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    return d.toISOString().split('T')[0];
  });
  const { data: sessionsForDate } = useTodaySessions(sessionDate);

  const pendingMakeups = makeups?.filter(m => m.status === 'pending') ?? [];
  const scheduledMakeups = makeups?.filter(m => m.status === 'scheduled') ?? [];
  const completedMakeups = makeups?.filter(m => m.status === 'completed' || m.status === 'waived') ?? [];

  const handleSchedule = async (makeupSessionId: string) => {
    if (!selectedMakeupId) return;
    await scheduleMakeup.mutateAsync({ makeupId: selectedMakeupId, makeupSessionId });
    setScheduleModalOpen(false);
    setSelectedMakeupId(null);
  };

  const handleComplete = async (makeupId: string) => {
    await updateStatus.mutateAsync({ makeupId, status: 'completed' });
  };

  const handleWaive = async (makeupId: string) => {
    await updateStatus.mutateAsync({ makeupId, status: 'waived', notes: 'Waived by coach' });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-24 w-full" />
        <Skeleton className="h-24 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
          <CalendarClock className="w-6 h-6 text-primary" />
          Makeup Sessions
        </h1>
        <p className="text-muted-foreground">Track and schedule makeup sessions for absent players</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-foreground">{pendingMakeups.length}</p>
            <p className="text-xs text-muted-foreground">Pending</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-foreground">{scheduledMakeups.length}</p>
            <p className="text-xs text-muted-foreground">Scheduled</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-3 text-center">
            <p className="text-xl font-bold text-foreground">{completedMakeups.length}</p>
            <p className="text-xs text-muted-foreground">Completed</p>
          </CardContent>
        </Card>
      </div>

      <MakeupsList
        title="Pending Makeups"
        description="These players need a makeup session scheduled"
        makeups={pendingMakeups}
        variant="pending"
        onSchedule={(makeupId) => {
          setSelectedMakeupId(makeupId);
          setScheduleModalOpen(true);
        }}
        onWaive={handleWaive}
      />

      <MakeupsList
        title="Scheduled Makeups"
        description="These makeups have a session assigned"
        makeups={scheduledMakeups}
        variant="scheduled"
        onComplete={handleComplete}
        onWaive={handleWaive}
      />

      <MakeupsList
        title="History"
        description=""
        makeups={completedMakeups}
        variant="history"
      />

      {/* Empty state */}
      {makeups?.length === 0 && (
        <Card>
          <CardContent className="p-8 text-center">
            <CalendarClock className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No makeup sessions yet</p>
            <p className="text-sm text-muted-foreground mt-1">
              Makeup sessions are created when you mark a player absent and flag them for a makeup.
            </p>
          </CardContent>
        </Card>
      )}

      <RequestMakeupSheet
        open={scheduleModalOpen}
        onOpenChange={setScheduleModalOpen}
        sessionDate={sessionDate}
        onSessionDateChange={setSessionDate}
        sessions={sessionsForDate}
        onSelectSession={handleSchedule}
        isPending={scheduleMakeup.isPending}
      />
    </div>
  );
}
